/* =========================================================
   RRR CATALOG SYSTEM
   Collection + Search Page

   Features:
   - AJAX filters
   - AJAX sorting
   - AJAX pagination
   - Mobile filter drawer
   - Desktop instant filtering
   - Mobile Apply button
   - Browser back/forward support
   - Request cancellation
   - Accessible focus handling
   ========================================================= */

(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-rrr-catalog]';
  const MOBILE_QUERY = window.matchMedia('(max-width: 749px)');
  const instances = new WeakMap();

  /**
   * Prevent the asset from registering global listeners twice.
   */
  if (window.RRRCatalogSystemLoaded) {
    initializeCatalogs(document);
    return;
  }

  window.RRRCatalogSystemLoaded = true;

  class RRRCatalog {
    constructor(root) {
      if (!(root instanceof HTMLElement)) return;

      if (root.dataset.rrrCatalogInitialized === 'true') {
        return;
      }

      this.root = root;
      this.sectionId = root.dataset.sectionId;

      this.abortController = null;
      this.priceTimer = null;
      this.lastDrawerTrigger = null;

      this.handleClick = this.handleClick.bind(this);
      this.handleChange = this.handleChange.bind(this);
      this.handleInput = this.handleInput.bind(this);
      this.handleSubmit = this.handleSubmit.bind(this);

      this.root.dataset.rrrCatalogInitialized = 'true';

      this.bindEvents();
      instances.set(this.root, this);
    }

    bindEvents() {
      this.root.addEventListener('click', this.handleClick);
      this.root.addEventListener('change', this.handleChange);
      this.root.addEventListener('input', this.handleInput);
      this.root.addEventListener('submit', this.handleSubmit);
    }

    isMobile() {
      return MOBILE_QUERY.matches;
    }

    getFilterPanel() {
      return this.root.querySelector('[data-rrr-filter-panel]');
    }

    getFilterOverlay() {
      return this.root.querySelector('[data-rrr-filter-overlay]');
    }

    getFilterForm() {
      return this.root.querySelector('[data-rrr-filter-form]');
    }

    getSortSelect() {
      return this.root.querySelector('[data-rrr-sort-select]');
    }

    getHiddenSortInput() {
      return this.root.querySelector('[data-rrr-hidden-sort]');
    }

    getLoadingElement() {
      return this.root.querySelector('[data-rrr-catalog-loading]');
    }

    handleClick(event) {
      const openButton = event.target.closest('[data-rrr-filter-open]');

      if (openButton && this.root.contains(openButton)) {
        event.preventDefault();
        this.openFilterDrawer(openButton);
        return;
      }

      const closeButton = event.target.closest('[data-rrr-filter-close]');

      if (closeButton && this.root.contains(closeButton)) {
        event.preventDefault();
        this.closeFilterDrawer();
        return;
      }

      const overlay = event.target.closest('[data-rrr-filter-overlay]');

      if (overlay && this.root.contains(overlay)) {
        event.preventDefault();
        this.closeFilterDrawer();
        return;
      }

      const ajaxLink = event.target.closest('[data-rrr-filter-link]');

      if (
        ajaxLink &&
        this.root.contains(ajaxLink) &&
        ajaxLink.href
      ) {
        event.preventDefault();

        const isPaginationLink = Boolean(
          ajaxLink.closest('.rrr-catalog-pagination')
        );

        this.loadCatalog(ajaxLink.href, {
          updateHistory: true,
          scrollToResults: isPaginationLink
        });
      }
    }

    handleChange(event) {
      const target = event.target;

      if (!(target instanceof HTMLElement)) return;

      /**
       * Sorting always applies immediately on desktop and mobile.
       */
      if (target.matches('[data-rrr-sort-select]')) {
        const hiddenSortInput = this.getHiddenSortInput();

        if (hiddenSortInput) {
          hiddenSortInput.value = target.value;
        }

        const filterForm = this.getFilterForm();

        if (filterForm) {
          this.applyFilterForm(filterForm);
        }

        return;
      }

      /**
       * Checkbox/list filters:
       * - Desktop: apply instantly
       * - Mobile: wait for Show Products button
       */
      if (target.matches('[data-rrr-filter-input]')) {
        if (this.isMobile()) return;

        if (
          target.matches('input[type="checkbox"]') ||
          target.matches('select')
        ) {
          const filterForm = this.getFilterForm();

          if (filterForm) {
            this.applyFilterForm(filterForm);
          }
        }
      }
    }

    handleInput(event) {
      const target = event.target;

      if (!(target instanceof HTMLInputElement)) return;

      if (!target.matches('[data-rrr-filter-input]')) return;
      if (target.type !== 'number') return;

      /**
       * Mobile users press the Apply button.
       */
      if (this.isMobile()) return;

      /**
       * Avoid requesting on every price digit.
       */
      window.clearTimeout(this.priceTimer);

      this.priceTimer = window.setTimeout(() => {
        const filterForm = this.getFilterForm();

        if (filterForm) {
          this.applyFilterForm(filterForm);
        }
      }, 650);
    }

    handleSubmit(event) {
      const form = event.target;

      if (!(form instanceof HTMLFormElement)) return;

      /**
       * Main filters form.
       */
      if (form.matches('[data-rrr-filter-form]')) {
        event.preventDefault();

        this.applyFilterForm(form);
        return;
      }

      /**
       * Search box inside the RRR search page.
       */
      if (form.matches('.rrr-catalog-search-form')) {
        event.preventDefault();

        const url = this.buildUrlFromForm(form, {
          includeSort: false
        });

        this.loadCatalog(url, {
          updateHistory: true,
          scrollToResults: false
        });
      }
    }

    applyFilterForm(form) {
      const url = this.buildUrlFromForm(form, {
        includeSort: true
      });

      this.loadCatalog(url, {
        updateHistory: true,
        scrollToResults: false
      });
    }

    buildUrlFromForm(form, options = {}) {
      const { includeSort = true } = options;

      const url = new URL(
        form.getAttribute('action') || window.location.pathname,
        window.location.origin
      );

      const formData = new FormData(form);
      const parameters = new URLSearchParams();

      for (const [name, value] of formData.entries()) {
        const cleanValue = String(value).trim();

        /**
         * Remove empty price fields and unused values.
         */
        if (!cleanValue) continue;

        parameters.append(name, cleanValue);
      }

      /**
       * Both the visible select and hidden input can contain sort_by.
       * set() removes duplicate sort parameters.
       */
      if (includeSort) {
        const sortSelect = this.getSortSelect();

        if (sortSelect && sortSelect.value) {
          parameters.set('sort_by', sortSelect.value);
        }
      }

      url.search = parameters.toString();
      url.hash = '';

      return url.toString();
    }

    openFilterDrawer(trigger) {
      if (!this.isMobile()) return;

      const panel = this.getFilterPanel();
      const overlay = this.getFilterOverlay();

      if (!panel) return;

      this.lastDrawerTrigger = trigger || null;

      panel.setAttribute('aria-hidden', 'false');

      if (trigger) {
        trigger.setAttribute('aria-expanded', 'true');
      }

      if (overlay) {
        overlay.hidden = false;
      }

      document.body.classList.add('rrr-catalog-filter-open');

      window.requestAnimationFrame(() => {
        const closeButton = panel.querySelector(
          '[data-rrr-filter-close]'
        );

        if (closeButton) {
          closeButton.focus();
        }
      });
    }

    closeFilterDrawer(options = {}) {
      const { restoreFocus = true } = options;

      const panel = this.getFilterPanel();
      const overlay = this.getFilterOverlay();
      const openButton = this.root.querySelector(
        '[data-rrr-filter-open]'
      );

      if (panel) {
        panel.setAttribute('aria-hidden', 'true');
      }

      if (openButton) {
        openButton.setAttribute('aria-expanded', 'false');
      }

      if (overlay) {
        overlay.hidden = true;
      }

      document.body.classList.remove('rrr-catalog-filter-open');

      if (
        restoreFocus &&
        this.lastDrawerTrigger &&
        document.contains(this.lastDrawerTrigger)
      ) {
        this.lastDrawerTrigger.focus();
      }

      this.lastDrawerTrigger = null;
    }

    handleGlobalKeydown(event) {
      const panel = this.getFilterPanel();

      if (
        !panel ||
        panel.getAttribute('aria-hidden') !== 'false'
      ) {
        return;
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeFilterDrawer();
        return;
      }

      /**
       * Keep keyboard focus inside the mobile drawer.
       */
      if (event.key !== 'Tab') return;

      const focusableElements = Array.from(
        panel.querySelectorAll(
          [
            'a[href]',
            'button:not([disabled])',
            'input:not([disabled])',
            'select:not([disabled])',
            'textarea:not([disabled])',
            '[tabindex]:not([tabindex="-1"])'
          ].join(',')
        )
      ).filter((element) => {
        return (
          element instanceof HTMLElement &&
          element.offsetParent !== null
        );
      });

      if (!focusableElements.length) return;

      const firstElement = focusableElements[0];
      const lastElement =
        focusableElements[focusableElements.length - 1];

      if (
        event.shiftKey &&
        document.activeElement === firstElement
      ) {
        event.preventDefault();
        lastElement.focus();
      } else if (
        !event.shiftKey &&
        document.activeElement === lastElement
      ) {
        event.preventDefault();
        firstElement.focus();
      }
    }

    setLoading(isLoading) {
      const loadingElement = this.getLoadingElement();

      this.root.classList.toggle('is-loading', isLoading);
      this.root.setAttribute(
        'aria-busy',
        isLoading ? 'true' : 'false'
      );

      if (loadingElement) {
        loadingElement.hidden = !isLoading;
      }
    }

    async loadCatalog(targetUrl, options = {}) {
      const {
        updateHistory = true,
        scrollToResults = false
      } = options;

      if (!this.sectionId) {
        window.location.href = targetUrl;
        return;
      }

      const browserUrl = new URL(
        targetUrl,
        window.location.origin
      );

      browserUrl.searchParams.delete('section_id');
      browserUrl.hash = '';

      const requestUrl = new URL(browserUrl.toString());
      requestUrl.searchParams.set(
        'section_id',
        this.sectionId
      );

      /**
       * Cancel the previous filter request when the user changes
       * another option quickly.
       */
      if (this.abortController) {
        this.abortController.abort();
      }

      this.abortController = new AbortController();

      this.closeFilterDrawer({
        restoreFocus: false
      });

      this.setLoading(true);

      try {
        const response = await fetch(requestUrl.toString(), {
          method: 'GET',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            Accept: 'text/html'
          },
          signal: this.abortController.signal
        });

        if (!response.ok) {
          throw new Error(
            `Catalog request failed: ${response.status}`
          );
        }

        const responseText = await response.text();
        const parser = new DOMParser();
        const responseDocument = parser.parseFromString(
          responseText,
          'text/html'
        );

        const newRoot =
          responseDocument.querySelector(
            `#RrrCatalog-${this.sectionId}`
          ) ||
          responseDocument.querySelector(ROOT_SELECTOR);

        if (!(newRoot instanceof HTMLElement)) {
          throw new Error(
            'Updated catalog section was not found.'
          );
        }

        /**
         * Replace only the custom RRR section.
         * Dawn header, footer and other theme sections remain untouched.
         */
        this.root.replaceWith(newRoot);

        if (updateHistory) {
          window.history.pushState(
            {
              rrrCatalog: true
            },
            '',
            `${browserUrl.pathname}${browserUrl.search}${browserUrl.hash}`
          );
        }

        const newInstance = initializeCatalog(newRoot);

        if (scrollToResults) {
          window.requestAnimationFrame(() => {
            const toolbar =
              newRoot.querySelector('.rrr-catalog-toolbar') ||
              newRoot.querySelector('[data-rrr-catalog-results]');

            if (toolbar) {
              const headerOffset = 20;
              const top =
                toolbar.getBoundingClientRect().top +
                window.scrollY -
                headerOffset;

              window.scrollTo({
                top,
                behavior: prefersReducedMotion()
                  ? 'auto'
                  : 'smooth'
              });
            }
          });
        }

        /**
         * Give screen readers a useful update after AJAX rendering.
         */
        if (newInstance) {
          const resultsCount = newRoot.querySelector(
            '[data-rrr-results-count]'
          );

          if (resultsCount) {
            resultsCount.setAttribute('tabindex', '-1');
          }
        }
      } catch (error) {
        if (error.name === 'AbortError') {
          return;
        }

        console.error('[RRR Catalog]', error);

        /**
         * Reliable fallback: use normal browser navigation if AJAX
         * ever fails.
         */
        window.location.href = browserUrl.toString();
      } finally {
        /**
         * The old root may already have been replaced.
         */
        if (document.contains(this.root)) {
          this.setLoading(false);
        }
      }
    }
  }

  function prefersReducedMotion() {
    return window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;
  }

  function initializeCatalog(root) {
    if (!(root instanceof HTMLElement)) return null;

    const existingInstance = instances.get(root);

    if (existingInstance) {
      return existingInstance;
    }

    return new RRRCatalog(root);
  }

  function initializeCatalogs(context = document) {
    if (
      context instanceof HTMLElement &&
      context.matches(ROOT_SELECTOR)
    ) {
      initializeCatalog(context);
    }

    context
      .querySelectorAll(ROOT_SELECTOR)
      .forEach((root) => initializeCatalog(root));
  }

  /**
   * Initial page load.
   */
  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => initializeCatalogs(document),
      { once: true }
    );
  } else {
    initializeCatalogs(document);
  }

  /**
   * Browser Back and Forward buttons.
   */
  window.addEventListener('popstate', () => {
    const root = document.querySelector(ROOT_SELECTOR);

    if (!(root instanceof HTMLElement)) return;

    const instance =
      instances.get(root) || initializeCatalog(root);

    if (!instance) return;

    instance.loadCatalog(window.location.href, {
      updateHistory: false,
      scrollToResults: false
    });
  });

  /**
   * Global keyboard handling for whichever mobile drawer is open.
   */
  document.addEventListener('keydown', (event) => {
    document
      .querySelectorAll(ROOT_SELECTOR)
      .forEach((root) => {
        const instance = instances.get(root);

        if (instance) {
          instance.handleGlobalKeydown(event);
        }
      });
  });

  /**
   * Close the mobile drawer automatically when switching to desktop.
   */
  MOBILE_QUERY.addEventListener('change', (event) => {
    if (event.matches) return;

    document
      .querySelectorAll(ROOT_SELECTOR)
      .forEach((root) => {
        const instance = instances.get(root);

        if (instance) {
          instance.closeFilterDrawer({
            restoreFocus: false
          });
        }
      });
  });

  /**
   * Shopify Theme Editor support.
   */
  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initializeCatalogs(event.target);
    }
  );
})();